import { useState } from "react";
import { modals } from "@mantine/modals";
import { Button, Group, Stack, TextInput } from "@mantine/core";

/**
 * Replacement for window.prompt — tiny single-input Mantine modal so the
 * editor never falls back to the browser-native dialog (which looks out of
 * place and is blocking). Used by slash commands and NodeView edit triggers
 * for blocks whose only attribute is a short title (titledFrame, sectionDivider).
 */
function TextPromptForm({
  initial,
  label,
  placeholder,
  submitLabel,
  modalId,
  resolve,
}: {
  initial: string;
  label: string;
  placeholder?: string;
  submitLabel: string;
  modalId: string;
  resolve: (val: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  // Order matters: resolve BEFORE close — onClose synchronously
  // settles with null otherwise, blocking the real payload.
  const submit = () => {
    resolve(value);
    modals.close(modalId);
  };
  const cancel = () => {
    resolve(null);
    modals.close(modalId);
  };
  return (
    <Stack gap="md">
      <TextInput
        size="sm"
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
        data-autofocus
      />
      <Group justify="flex-end">
        <Button variant="default" size="xs" onClick={cancel}>
          Cancel
        </Button>
        <Button size="xs" onClick={submit}>
          {submitLabel}
        </Button>
      </Group>
    </Stack>
  );
}

export function openTextPrompt(options: {
  title: string;
  label: string;
  initial?: string;
  placeholder?: string;
  submitLabel?: string;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const modalId = `text-prompt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const settle = (val: string | null) => {
      if (settled) return;
      settled = true;
      resolve(val);
    };
    modals.open({
      modalId,
      title: options.title,
      size: "sm",
      withCloseButton: true,
      closeOnEscape: true,
      closeOnClickOutside: false,
      onClose: () => settle(null),
      children: (
        <TextPromptForm
          initial={options.initial ?? ""}
          label={options.label}
          placeholder={options.placeholder}
          submitLabel={options.submitLabel ?? "Insert"}
          modalId={modalId}
          resolve={settle}
        />
      ),
    });
  });
}
